import type { VolumeTier } from '@/api/usePrices'

export interface TierResult {
    effectivePrice: number
    basePrice: number
    discount: number        // percentage
    tierMinMillares: number | null
    nextTier: { minMillares: number; price: number; savings: number } | null
}

/**
 * Calculate the effective price per millar based on volume tiers.
 */
export function calculateTierPrice(
    basePricePerMillar: number,
    quantity: number,
    tiers: VolumeTier[]
): TierResult {
    if (!tiers || tiers.length === 0) {
        return {
            effectivePrice: basePricePerMillar,
            basePrice: basePricePerMillar,
            discount: 0,
            tierMinMillares: null,
            nextTier: null,
        }
    }

    // Sort tiers ascending by min_millares
    const sorted = [...tiers]
        .map(t => ({ minMillares: t.min_millares, price: parseFloat(t.price_per_millar) }))
        .sort((a, b) => a.minMillares - b.minMillares)

    // Find best matching tier (highest min_millares <= quantity)
    let effectivePrice = basePricePerMillar
    let tierMinMillares: number | null = null

    for (const tier of sorted) {
        if (quantity >= tier.minMillares) {
            effectivePrice = tier.price
            tierMinMillares = tier.minMillares
        }
    }

    // Find next tier (first tier where min > quantity)
    let nextTier: TierResult['nextTier'] = null
    for (const tier of sorted) {
        if (tier.minMillares > quantity) {
            const savings = effectivePrice - tier.price
            if (savings > 0) {
                nextTier = {
                    minMillares: tier.minMillares,
                    price: tier.price,
                    savings,
                }
            }
            break
        }
    }

    const discount = basePricePerMillar > 0 && effectivePrice < basePricePerMillar
        ? ((basePricePerMillar - effectivePrice) / basePricePerMillar) * 100
        : 0

    return {
        effectivePrice,
        basePrice: basePricePerMillar,
        discount: Math.round(discount * 100) / 100,
        tierMinMillares,
        nextTier,
    }
}
